import React, {useEffect, useState} from "react";
import {Lead, LeadRestControllerApiFactory, Organization} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    TextField,
} from "@mui/material";
import {FormModel, useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import {types} from "../../../../components/form/types";
import {FormButtonProps} from "../../../../components/form/form_inputs/form_button";

export const LeadEditPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(LeadRestControllerApiFactory);

    const [convertOpen, setConvertOpen] = useState(false);
    const [orgOptions, setOrgOptions] = useState<Organization[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const [orgTerm, setOrgTerm] = useState("");

    const form: FormModel<Lead> = useForm({
        fetcher: (id) => ctrl.getLead(id),
        updater: (bean) => ctrl.saveLead(bean),
        remover: (id) => ctrl.deleteOrDismissLead(id),
        factory: () => ({} as Lead),
        extraActions: [
            {
                label: "Convertir a contacto",
                disabled: () => !form.propModel.value?.id,
                action: () => setConvertOpen(true),
            } as FormButtonProps,
        ],
    } as UseFormProps<Lead>);

    useEffect(() => {
        if (!convertOpen) return;
        types.organizations.supplier(apiManager, orgTerm).then(list => setOrgOptions(list));
    }, [convertOpen, orgTerm]);

    const handleConvert = () => {
        if (!selectedOrg?.id) return;
        apiManager.execute(ctrl.convertLead(form.getIdOrErr(), selectedOrg.id), "Lead convertido")
            .then((lead) => {
                form.setter(lead);
                setConvertOpen(false);
                setSelectedOrg(null);
            });
    };

    return (
        <FormContent form={form} title={"Lead"}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    {/* Email, teléfono y LinkedIn son opcionales por separado, pero hace falta uno
                        de los tres: el backend lo valida (Lead.isReachable). Las referencias del
                        canal de conectores llegan con teléfono y sin email —BNI Connect no pide
                        email— y se quedaban fuera mientras el email fue obligatorio (#11395); lo
                        mismo pasaba con quien solo responde por LinkedIn (#12943). */}
                    <FormInput field={"email"} label={"Email"} type="email"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"phone"} label={"Teléfono"}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"name"} label={"Nombre"}/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                    <FormInput field={"linkedin"}
                               label={"LinkedIn — hace falta al menos uno de los tres: email, teléfono o este"}/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"organizationName"} label={"Organización (texto)"}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormSelect field={"source"} label={"Origen"} type={types.leadSources}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormSelect field={"status"} label={"Estado"} type={types.leadStatuses}/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"lastTouchDate"} label={"Último toque"} type="date"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"touchCount"} label={"Nº de toques"} type="number"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"nextActionDate"} label={"Próxima acción"} type="date"/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"segment"} label={"Segmento"}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"abVariant"} label={"Variante A/B"}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"productOffered"} label={"Producto ofertado"}/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                    {/* Quién derivó el lead. Es lo que permite medir el ROI por conector: sin
                        esto, `source = REFERRAL` dice que alguien lo trajo pero no quién. */}
                    <FormAutocomplete field={"referredBy"} type={types.contacts}
                                      label={"Derivado por (conector) — solo si el origen es Referido"}/>
                </Grid>
            </Grid>

            {/* Cualificación (#11788): los cuatro datos que el proceso manda preguntar antes de
                presupuestar (comercial/wf-retirada-de-cliente.md §6.2). Deliberadamente NO hay
                campos de presupuesto, gasto en software ni intención de compra: son preguntas que
                el cliente lee como tasación y que el proceso prohíbe hacer. Los selects nacen sin
                valor — "sin preguntar" es distinto de "no" — así que ninguno se marca obligatorio. */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormSelect field={"inOperation"} label={"¿En operación?"} type={types.leadOperationStages}/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormSelect field={"maintainedBy"} label={"Quién lo mantiene"} type={types.leadMaintainers}/>
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"deadlineDate"} label={"Fecha que obliga"} type="date"/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"deadlineSource"} label={"De dónde sale esa fecha"}/>
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                    <FormInput field={"decisionMakers"} label={"Quién más tiene que decir que sí"}/>
                </Grid>
            </Grid>

            {/* Base legal del correo comercial (#13647, LSSI art. 21). El boletín solo va a
                CONSENT, que exige fecha y origen; una baja es OPTED_OUT y gana a todo. Vacío =
                sin revisar. Reglas: arteco-docs/procesos/marketing/wf-outbound-leads.md. */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormSelect field={"commercialEmailBasis"} label={"Base legal del correo"} type={types.commercialEmailBases}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormInput field={"commercialEmailBasisDate"} label={"Desde"} type="date"/>
                </Grid>
                <Grid item xs={12} sm={5}>
                    <FormInput field={"commercialEmailBasisSource"} label={"Origen de la base legal"}/>
                </Grid>
            </Grid>

            <FormInput field={"interests"} label={"Intereses"} rows={3}/>
            <FormInput field={"observation"} label={"Observaciones"} rows={3}/>

            <Dialog open={convertOpen} onClose={() => setConvertOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Convertir lead a contacto</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        El lead se convertirá en un contacto de la organización seleccionada.
                        Si la organización no existe todavía, créala antes desde el módulo de organizaciones.
                    </DialogContentText>
                    <Autocomplete
                        fullWidth
                        options={orgOptions}
                        value={selectedOrg}
                        filterOptions={(x) => x}
                        isOptionEqualToValue={(option, val) => option.id === val.id}
                        getOptionLabel={(option) => option.name ?? ""}
                        onChange={(_e, val) => setSelectedOrg(val)}
                        onInputChange={(_e, val) => setOrgTerm(val)}
                        renderInput={(params) => (
                            <TextField {...params} label="Organización destino" autoFocus/>
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConvertOpen(false)}>Cancelar</Button>
                    <Button variant="contained" disabled={!selectedOrg?.id} onClick={handleConvert}>
                        Convertir
                    </Button>
                </DialogActions>
            </Dialog>
        </FormContent>
    );
};
